import { bufferToHex } from '@exodus/ethereumjs/util'
import SolidityContract from '@exodus/solidity-contract'
import EventEmitter from 'events'
import io from 'socket.io-client'

import { fromHexToString } from '../number-utils.js'

export default class ClarityServer extends EventEmitter {
  constructor({ baseAssetName, uri }) {
    super()
    this.baseAssetName = baseAssetName
    this.uri = uri
    this.defaultUri = uri
    this.baseNamespace = `/v1/${this.baseAssetName}`
    this.sockets = Object.create(null)
    this.id = 0
  }

  setURI(uri) {
    this.dispose()
    this.uri = uri
  }

  connectTransactions({ walletAccount, address }) {
    const namespace = this.formatTransactionsNamespace(address)
    if (!this.sockets[namespace]) {
      this.sockets[namespace] = this.createSocket(namespace)
      this.sockets[namespace].on('transaction', (isPending, transaction) => {
        this.emit('transaction', { walletAccount, isPending, transaction })
      })
    }

    return this.sockets[namespace]
  }

  connectRpc() {
    const namespace = this.formatRpcNamespace()
    if (!this.sockets[namespace]) {
      this.sockets[namespace] = this.createSocket(namespace)
    }

    return this.sockets[namespace]
  }

  connectFee() {
    const namespace = this.formatFeeNamespace()
    if (!this.sockets[namespace]) {
      this.sockets[namespace] = this.createSocket(namespace)
      this.sockets[namespace].on('feeUpdated', (fee) => this.emit('feeUpdated', fee))
    }

    return this.sockets[namespace]
  }

  createSocket(namespace) {
    return io(`${this.uri}${namespace}`, {
      transports: ['websocket', 'polling'],
      extraHeaders: { 'User-Agent': 'exodus' },
    })
  }

  disconnectTransactions(address) {
    const namespace = this.formatTransactionsNamespace(address)
    this.disconnectSocket(namespace)
  }

  disconnectRpc() {
    this.disconnectSocket(this.rpcNamespace)
  }

  disconnectFee() {
    this.disconnectSocket(this.feeNamespace)
  }

  disconnectSocket(namespace) {
    if (this.sockets[namespace]) {
      this.sockets[namespace].disconnect()
      delete this.sockets[namespace]
    }
  }

  dispose() {
    const namespaces = Object.keys(this.sockets)
    for (const namespace of namespaces) {
      this.disconnectSocket(namespace)
    }
  }

  // See https://www.jsonrpc.org/specification#response_object for details.
  handleJsonRPCResponse(response) {
    const result = response?.result
    const error = response?.error
    if (error || result === undefined) {
      const message = error?.message || error?.code || 'no result'
      throw new Error(`Bad rpc response: ${message}`)
    }

    return result
  }

  async getAllTransactions(params) {
    const transactions = { pending: [], confirmed: [] }
    const cursor = await this.getTransactions({
      ...params,
      onChunk: (isPending, chunk) => {
        const mapped = chunk.map((serverTx) => ({
          ...serverTx,
          gasPrice: serverTx.gasPriceEffective || serverTx.gasPrice,
        }))
        if (isPending) {
          transactions.pending.push(...mapped)
        } else {
          transactions.confirmed.push(...mapped)
        }
      },
    })
    return { cursor, transactions }
  }

  async getTransactions({ walletAccount, address, cursor, onChunk }) {
    const socket = this.connectTransactions({ walletAccount, address })
    const listener = (isPending, chunk, callback) => {
      onChunk(isPending, chunk)
      callback()
    }

    socket.on('transactionsChunk', listener)
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Transactions Timeout')), 300_000)
      socket.emit('getTransactions', cursor, (nextCursor) => {
        clearTimeout(timeout)
        const uintArray = new Uint8Array(nextCursor)
        resolve(Buffer.from(uintArray))
      })
    })
      .catch((error) => console.error(error))
      .finally(() => socket.off('transactionsChunk', listener))
  }

  async getFee() {
    const socket = this.connectFee()
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Fee Timeout')), 30_000)
      socket.emit('getFee', (fee) => {
        clearTimeout(timeout)
        if (!fee) {
          const error = new Error('Unable to get fee')
          return reject(error)
        }

        resolve(fee)
      })
    })
  }

  // for fee monitors
  async getGasPrice() {
    const fee = await this.getFee()
    return fee?.gasPrice
  }

  async sendRpcRequest(rpcRequest) {
    const rpcSocket = this.connectRpc()
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Rpc Timeout')), 30_000)
      rpcSocket.emit('request', rpcRequest, (response) => {
        clearTimeout(timeout)
        resolve(response)
      })
    })
  }

  async sendBatchRequest(batch) {
    const responses = await this.sendRpcRequest(batch)
    // FIXME: this falls apart if responses is not an array
    const isValid = responses.every((response) => {
      return !isNaN(response?.id) && response?.result
    })
    if (responses.length !== batch.length || !isValid) {
      throw new Error('Bad Response')
    }

    const keyed = responses.reduce((acc, response) => {
      return { ...acc, [`${response.id}`]: response.result }
    }, Object.create(null))
    return batch.map((request) => keyed[`${request.id}`])
  }

  async sendRequest(request) {
    const response = await this.sendRpcRequest(request)

    return this.handleJsonRPCResponse(response)
  }

  async isContract(address) {
    const code = await this.getCode(address)
    return code.length > 2
  }

  buildRequest({ method, params = [] }) {
    return { jsonrpc: '2.0', id: this.id++, method, params }
  }

  balanceOfRequest(address, tokenAddress, tag = 'latest') {
    const contract = SolidityContract.simpleErc20(tokenAddress)
    const callData = contract.balanceOf.build(address)
    const data = {
      data: bufferToHex(callData),
      to: tokenAddress,
    }
    return this.ethCallRequest(data, tag)
  }

  getBalanceRequest(address, tag = 'latest') {
    return this.buildRequest({ method: 'eth_getBalance', params: [address, tag] })
  }

  gasPriceRequest() {
    return this.buildRequest({ method: 'eth_gasPrice' })
  }

  estimateGasRequest(data, tag = 'latest') {
    return this.buildRequest({ method: 'eth_estimateGas', params: [data, tag] })
  }

  sendRawTransactionRequest(data) {
    const _data = data instanceof Uint8Array ? Buffer.from(data).toString('hex') : data
    const hex = _data.startsWith('0x') ? _data : '0x' + _data
    return this.buildRequest({ method: 'eth_sendRawTransaction', params: [hex] })
  }

  coinbaseRequest() {
    return this.buildRequest({ method: 'eth_coinbase' })
  }

  getCodeRequest(address, tag = 'latest') {
    return this.buildRequest({ method: 'eth_getCode', params: [address, tag] })
  }

  getStorageAtRequest(address, position, tag = 'latest') {
    return this.buildRequest({ method: 'eth_getStorageAt', params: [address, position, tag] })
  }

  getTransactionCountRequest(address, tag = 'latest') {
    return this.buildRequest({ method: 'eth_getTransactionCount', params: [address, tag] })
  }

  getTransactionByHashRequest(hash) {
    return this.buildRequest({ method: 'eth_getTransactionByHash', params: [hash] })
  }

  getTransactionReceiptRequest(txhash) {
    return this.buildRequest({ method: 'eth_getTransactionReceipt', params: [txhash] })
  }

  ethCallRequest(data, tag = 'latest') {
    return this.buildRequest({ method: 'eth_call', params: [data, tag] })
  }

  blockNumberRequest() {
    return this.buildRequest({ method: 'eth_blockNumber' })
  }

  getLogsRequest(object) {
    return this.buildRequest({ method: 'eth_getLogs', params: [object] })
  }

  getBlockByNumberRequest(numberHex, isFullTxs = false) {
    return this.buildRequest({ method: 'eth_getBlockByNumber', params: [numberHex, isFullTxs] })
  }

  getBlockByHashRequest(blockHash, isFullTxs = false) {
    return this.buildRequest({ method: 'eth_getBlockByHash', params: [blockHash, isFullTxs] })
  }

  getBlockTransactionCountByHashRequest(blockHash) {
    return this.buildRequest({ method: 'eth_getBlockTransactionCountByHash', params: [blockHash] })
  }

  getBlockTransactionCountByNumberRequest(quantityOrTag) {
    return this.buildRequest({
      method: 'eth_getBlockTransactionCountByNumber',
      params: [quantityOrTag],
    })
  }

  getCompilersRequest() {
    return this.buildRequest({ method: 'eth_getCompilers' })
  }

  getNetVersionRequest() {
    return this.buildRequest({ method: 'net_version' })
  }

  simulateRawTransactionRequest(rawTx, applyPending = true) {
    const replaced = rawTx.replace('0x', '')
    return this.buildRequest({
      method: 'debug_simulateRawTransaction',
      params: [replaced, applyPending],
    })
  }

  async proxyToCoinNode(params) {
    const request = this.buildRequest(params)
    return this.sendRequest(request)
  }

  async balanceOf(address, tokenAddress, tag = 'latest') {
    const request = this.balanceOfRequest(address, tokenAddress, tag)
    const result = await this.sendRequest(request)
    const balance = fromHexToString(result)
    return {
      confirmed: {
        [tokenAddress]: balance,
      },
    }
  }

  async getBalance(...params) {
    const request = this.getBalanceRequest(...params)
    return this.sendRequest(request)
  }

  async getBalanceProxied(...params) {
    return this.getBalance(...params)
  }

  async gasPrice(...params) {
    const request = this.gasPriceRequest(...params)
    return this.sendRequest(request)
  }

  async estimateGas(...params) {
    const request = this.estimateGasRequest(...params)
    return this.sendRequest(request)
  }

  async sendRawTransaction(...params) {
    const request = this.sendRawTransactionRequest(...params)
    return this.sendRequest(request)
  }

  async getCode(...params) {
    const request = this.getCodeRequest(...params)
    return this.sendRequest(request)
  }

  async getStorageAt(...params) {
    const request = this.getStorageAtRequest(...params)
    return this.sendRequest(request)
  }

  async getTransactionCount(...params) {
    const request = this.getTransactionCountRequest(...params)
    return this.sendRequest(request)
  }

  async getTransactionByHash(...params) {
    const request = this.getTransactionByHashRequest(...params)
    return this.sendRequest(request)
  }

  async getTransactionReceipt(...params) {
    const request = this.getTransactionReceiptRequest(...params)
    return this.sendRequest(request)
  }

  async ethCall(...params) {
    const request = this.ethCallRequest(...params)
    return this.sendRequest(request)
  }

  async blockNumber(...params) {
    const request = this.blockNumberRequest(...params)
    return this.sendRequest(request)
  }

  async getLatestBlock() {
    return this.getBlockByNumber('latest')
  }

  async getBaseFeePerGas() {
    const response = await this.getLatestBlock()
    if (response.baseFeePerGas) {
      return fromHexToString(response.baseFeePerGas)
    }
  }

  async getBlockByHash(...params) {
    const request = this.getBlockByHashRequest(...params)
    return this.sendRequest(request)
  }

  async getBlockTransactionCountByNumber(...params) {
    const request = this.getBlockTransactionCountByNumberRequest(...params)
    return this.sendRequest(request)
  }

  async getBlockByNumber(...params) {
    const request = this.getBlockByNumberRequest(...params)
    return this.sendRequest(request)
  }

  async simulateRawTransaction(...params) {
    const request = this.simulateRawTransactionRequest(...params)
    return this.sendRequest(request)
  }

  async getCoinbase() {
    const request = this.coinbaseRequest()
    return this.sendRequest(request)
  }

  async getCompilers() {
    const request = this.getCompilersRequest()
    return this.sendRequest(request)
  }

  async getNetVersion() {
    const request = this.getNetVersion()
    return this.sendRequest(request)
  }

  async getLogs(...params) {
    const request = this.getLogsRequest(...params)
    return this.sendRequest(request)
  }

  formatTransactionsNamespace(address) {
    return `${this.baseNamespace}/addresses/${address}/transactions`
  }

  formatRpcNamespace() {
    return `${this.baseNamespace}/rpc`
  }

  formatFeeNamespace() {
    return `${this.baseNamespace}/fee`
  }

  stop() {
    this.dispose()
  }
}
